import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { centerUnderPointer } from '@atlaskit/pragmatic-drag-and-drop/element/center-under-pointer';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { Box } from '@mui/material';
import type { PlanningEntityType } from 'generated';

import type { Event } from '../../types';

import { AppointmentChipPragmatic } from './AppointmentChipPragmatic';
import MultiDragPreviewPragmatic from '../MultiDragPreviewPragmatic';

// Strip absolute-positioning so the clone renders inside the detached preview box.
function resetClonedWrapperStyles(clone: HTMLElement, source: HTMLElement) {
  clone.style.position = 'static';
  clone.style.left = 'auto';
  clone.style.top = 'auto';
  clone.style.zIndex = 'auto';
  clone.style.opacity = '1';
  clone.style.width = `${source.offsetWidth}px`;
  clone.style.height = `${source.offsetHeight}px`;
}

import { ZoomLevel as DetailZoomLevel } from '../../components/Header/ZoomLevelMenu.enums';

interface ConditionalDraggableChipPragmaticProps extends Event {
  width: number;
  startingPoint: number;
  /** True while this chip is the frozen drop source; keeps it faded (no snap-back). */
  isFrozen?: boolean;
  planningEntityId: string;
  planningEntityType?: PlanningEntityType;
  scheduleStart?: string;
  selected?: boolean;
  isToPlan: boolean;
  /** Ref (not number) so selection-count changes don't re-render the chip. */
  selectionCountRef?: RefObject<number>;
  onSelectAppointment?: (id: string) => void;
  /** Lifted to one subscription in `AppointmentsPragmatic` (~5 000 → 1). */
  detailZoomLevel: DetailZoomLevel;
}

// draggable() is registered once on mount; the drag preview clones live DOM.
const ConditionalDraggableChipPragmatic = (
  props: ConditionalDraggableChipPragmaticProps
) => {
  'use memo';
  // Strip wrapper-only props so `...eventProps` stays a stable Event identity (no chip re-render).
  const {
    startingPoint,
    isFrozen,
    planningEntityId,
    planningEntityType: _planningEntityType,
    scheduleStart,
    isToPlan,
    selectionCountRef: externalSelectionCountRef,
    onSelectAppointment,
    detailZoomLevel,
    ...eventProps
  } = props;
  const { id, isAbsent, width, selected } = eventProps;

  const [hovered, setHoveredState] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDisabled, setDragDisabled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Ref fed to the mount-once draggable() closure so it stays current.
  const canDragRef = useRef(false);

  const setHovered = (value: boolean) => {
    setHoveredState(value);
  };

  // chipWidth flows into drag data so the tooltip can anchor at the chip's top-left.
  const dragDataRef = useRef({
    id,
    planningEntityId,
    scheduleStart,
    isToPlan,
    chipWidth: width
  });

  const isSelectedRef = useRef(!!selected);

  // Keep the refs above current for the mount-once draggable() callbacks.
  // Assigned in a layout effect (never during render) so the React Compiler can
  // optimize this component.
  useLayoutEffect(() => {
    canDragRef.current = !isAbsent && !dragDisabled;
    dragDataRef.current = {
      id,
      planningEntityId,
      scheduleStart,
      isToPlan,
      chipWidth: width
    };
    isSelectedRef.current = !!selected;
  });

  // Register draggable() once; refs feed always-current values to callbacks.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    return draggable({
      element: el,
      canDrag: () => canDragRef.current,
      getInitialData: () => ({ ...dragDataRef.current }),
      onGenerateDragPreview: ({ nativeSetDragImage, source }) => {
        const count = externalSelectionCountRef?.current ?? 0;
        const isMulti = isSelectedRef.current && count > 1;

        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: centerUnderPointer,
          render: ({ container }) => {
            if (isMulti) {
              // flushSync commits before Pragmatic's queueMicrotask snapshots.
              const root = createRoot(container);
              flushSync(() =>
                root.render(<MultiDragPreviewPragmatic count={count} />)
              );
              return () => root.unmount();
            }
            // Clone live DOM — re-rendering here would throw (detached tree has no providers).
            const clone = source.element.cloneNode(true) as HTMLElement;
            resetClonedWrapperStyles(clone, source.element);
            container.appendChild(clone);
          }
        });
      },
      onDragStart: () => {
        setIsDragging(true);
      },
      onDrop: () => {
        setIsDragging(false);
        setHoveredState(false);
      }
    });
  }, [externalSelectionCountRef]);

  const style = {
    position: 'absolute' as const,
    left: startingPoint,
    top:
      detailZoomLevel === DetailZoomLevel.COMPACT ? '50%' : 'calc(50% - 10px)',
    zIndex: isAbsent ? 1 : 2,
    display: 'inline-block'
  };

  return (
    <Box
      ref={containerRef}
      style={style}
      // Read by the CSS rule below to fade this chip during a multi-drag.
      data-selected={selected || undefined}
      data-appointment-chip
      onClick={() => onSelectAppointment?.(id)}
      onMouseDown={() => setHovered(true)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        // Don't clear hover mid-drag — DOM check avoids subscribing to an atom.
        if (!containerRef.current?.closest('[data-drag-active]')) {
          setHovered(false);
        }
      }}
      sx={{
        cursor: !isAbsent && !dragDisabled ? 'grab' : 'default',
        opacity: isDragging || isFrozen ? 0.5 : 1,
        // Fade selected chips during a multi-drag via pure CSS (no atom).
        '[data-drag-active] &[data-selected]': { opacity: 0.5 },
        ...(!isAbsent && {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '-3px',
            zIndex: -1
          }
        })
      }}
    >
      <AppointmentChipPragmatic
        {...eventProps}
        hovered={hovered}
        setHovered={setHovered}
        setDisabled={setDragDisabled}
        isDragging={isDragging}
        detailZoomLevel={detailZoomLevel}
      />
    </Box>
  );
};

export default ConditionalDraggableChipPragmatic;
